// The single source of truth for FBC's site tree (see /app/memory/SITEMAP.md).
// Both the desktop sidebar and the mobile nav render from this. 5 trunks, 3-7 limbs each.
import { TOPICS, topicSection } from '@/lib/topics';
import {
  Building2, BookOpen, Calendar, Home, Gavel, Wallet, Megaphone, ClipboardList, Archive, HardHat,
  Users, Landmark, MessageCircle, ShoppingBag, Scale, BadgePercent, ShieldCheck, Briefcase,
  Search, ClipboardCheck, Calculator, Route, Newspaper, PlayCircle, GraduationCap,
} from 'lucide-react';

export const NAV_TREE = [
  {
    id: 'project', label: 'My Project', subtitle: 'Everything about your project', icon: Building2, color: 'primary',
    landing: 'project-overview',
    limbs: [
      { id: 'project-overview', label: 'Overview', icon: BookOpen, hint: 'The story so far & what is live' },
      { id: 'project-delivery', label: 'Delivery Risk', icon: HardHat, hint: 'Will it be completed on time?', when: (c) => !c.isPostOc },
      { id: 'project-litigation', label: 'Litigation', icon: Gavel, hint: 'Allottee & project disputes' },
      { id: 'project-money', label: 'Financial Details', icon: Wallet, hint: 'Funding gap, collections & AI review' },
      { id: 'project-dev-info', label: 'Developer Assurances', icon: Megaphone, hint: 'On-time delivery & money to finish' },
      { id: 'project-buyer-info', label: 'RERA Records', icon: ClipboardList, hint: 'Construction, sanctions, Forms 1/2/3 & promoter' },
      { id: 'project-archive', label: 'Archive & Vault', icon: Archive, hint: 'History & documents' },
    ],
  },
  {
    id: 'group', label: "Buyers' Group", subtitle: 'Strength in numbers', icon: Users, color: 'green',
    landing: 'community-landing', gated: true,
    limbs: [
      { id: 'us-directory', label: 'Members', icon: Users, hint: 'Who is in your group' },
      { id: 'property-marketplace', label: 'Classifieds', icon: ShoppingBag, hint: 'Buy, rent or lease within the project' },
      // Topic rooms are reached from the Buyers' Group main page (not listed in menus).
      ...TOPICS.map((t) => ({ id: topicSection(t.key), label: t.label, icon: t.icon, hint: t.hint, inMenu: false })),
    ],
  },
  {
    id: 'rights', label: 'Rights and Options', subtitle: 'Understand your rights & choices', icon: Scale, color: 'amber',
    landing: 'recourse-landing',
    limbs: [
      { id: 'legal-services', label: 'S.18 Interest & Refund: How it works', icon: Scale, hint: 'Your options, alone or together', also: ['us-s18-collective'] },
      { id: 'subvention-claims', label: 'Subvention & Pre-EMI Claims', icon: BadgePercent, hint: 'When the developer stops paying EMIs' },
      { id: 'post-possession-rights', label: 'Post-Possession Rights', icon: ShieldCheck, hint: 'Corpus, maintenance, defects, conveyance' },
      { id: 'service-providers', label: 'Find Independent Professionals', icon: Briefcase, hint: 'FBC does not represent you' },
    ],
  },
  {
    id: 'buying', label: 'Buying Smart', subtitle: 'Before you commit', icon: Search, color: 'blue',
    landing: 'buying-landing',
    limbs: [
      { id: 'browse-projects', label: 'Search Projects', icon: Search, hint: 'Find any RERA project' },
      { id: 'risk-landing', label: "Buyer's Checklist", icon: ClipboardCheck, hint: 'Financial, approvals, title, loans' },
      { id: 'emi-calculator', label: 'EMI Calculator', icon: Calculator, hint: 'What it will really cost' },
      { id: 'buyers-journey', label: "Buyer's Journey", icon: Route, hint: 'Step by step, booking to keys' },
    ],
  },
  {
    id: 'learn', label: 'Knowledge Hub', subtitle: 'News, guidance & buyer resources', icon: GraduationCap, color: 'slate',
    landing: 'learn-landing',
    limbs: [
      { id: 'news', label: 'Real Estate News & Analysis', icon: Newspaper, hint: 'News & media on buyer rights and the market' },
      { id: 'education', label: 'Expert Guidance & Education', icon: PlayCircle, hint: 'Guides, articles, videos & FAQs' },
      { id: 'buyer-resources', label: 'Free Buyer Resources & Legal Reference', icon: BookOpen, hint: 'Legal rights, case law, checklists & downloads' },
    ],
  },
];

// Old section ids → their single new home (old links keep working).
// Same fallback the project page uses when no project is selected yet.
export const DEFAULT_PROJECT_ID = 'ebdab734-e651-45f8-969f-c1bbe525503e';

export const SECTION_REDIRECTS = {
  'us-threads': 'community-landing',
  'us-meetings': 'community-landing',
  'us-correspondence': 'community-landing',
  'us-notices': 'community-landing',
  'project-living': 'community-landing',
  'corpus-warroom': 'topic-corpus',
  'project-rera': 'project-overview',
  'project-rera-public': 'project-overview',
  'project-record': 'project-overview',
  'project-story': 'project-overview',
  'project': 'project-overview',
  'project-details': 'project-buyer-info',
  'project-progress': 'project-dev-info',
  'document-vault': 'project-archive',
  'quality-issues': 'post-possession-rights',
  'group-buying': 'community-landing',
  'project-hub': 'project-overview',
  'us': 'us-directory',
  'toolkit': 'risk-landing',
  'knowledge': 'education',
  'videos': 'education',
};

// Which trunk a section belongs to (for auto-expanding the right branch).
export const trunkOf = (section) => {
  const s = SECTION_REDIRECTS[section] || section;
  if (s?.startsWith('project-')) return 'project';
  const t = NAV_TREE.find((tr) => tr.landing === s || tr.limbs.some((l) => l.id === s || l.also?.includes(s)));
  return t?.id || null;
};

// Human label for any section id (limb label, or trunk label for landings).
export const sectionLabel = (section) => {
  const s = SECTION_REDIRECTS[section] || section;
  for (const t of NAV_TREE) {
    const l = t.limbs.find((x) => x.id === s || x.also?.includes(s));
    if (l) return l.label;
    if (t.landing === s) return t.label;
  }
  return null;
};

export const visibleLimbs = (trunk, ctx) => trunk.limbs.filter((l) => l.inMenu !== false && (!l.when || l.when(ctx)));

// Breadcrumb trail for a section: [{label, section}] from Home down to the current page.
export const breadcrumbFor = (section, fallbackLabel) => {
  const crumbs = [{ label: 'Home', section: 'home' }];
  if (!section || section === 'home') return crumbs;
  const t = NAV_TREE.find((x) => x.id === trunkOf(section));
  if (t) {
    crumbs.push({ label: t.label, section: t.landing });
    const label = sectionLabel(section);
    if (label && label !== t.label && (SECTION_REDIRECTS[section] || section) !== t.landing) crumbs.push({ label, section });
  } else if (fallbackLabel) {
    crumbs.push({ label: fallbackLabel, section });
  }
  return crumbs;
};

// One canonical name per module everywhere (sidebar, menus, landing pages, breadcrumbs).
export const trunkLabel = (trunk) => trunk.label;
export const isReferenceView = (projectId, user) => projectId === DEFAULT_PROJECT_ID && user?.project_id !== DEFAULT_PROJECT_ID;
